/** OTA-webbundels: de app haalt nieuwe UI op zonder nieuwe APK.
 *  Dezelfde nginx die de site host serveert /updates/manifest.json +
 *  de zip uit dezelfde `dist/`; deze module vergelijkt bij starten en
 *  bij terugkeer uit de achtergrond, downloadt stil en zet de bundel
 *  klaar voor de VOLGENDE start (nooit midden in een sessie wisselen).
 *  `notifyAppReady()` in main.tsx na het mounten is het vangnet: blijft
 *  die uit, dan rolt de plugin terug. Web (browser) doet hier niets.
 *  docs/customer-app.md > Front-end > OTA web bundles
 */

import { API_BASE, setAppVersions } from "./api";

type Manifest = {
  version: string;
  path: string;
  /** Minimale native versie (versionName) die deze bundel nodig heeft.
   *  Zo kan een bundel die een nieuwe plugin gebruikt niet landen op
   *  een oude schil - die zou dan wit blijven. */
  min_native?: string;
};

type Updater = {
  notifyAppReady: () => Promise<unknown>;
  current: () => Promise<{ bundle: { version: string; id: string }; native: string }>;
  download: (o: { url: string; version: string }) => Promise<{ id: string }>;
  next: (o: { id: string }) => Promise<unknown>;
  list: () => Promise<{ bundles: { id: string; version: string }[] }>;
  delete: (o: { id: string }) => Promise<unknown>;
};

function plugin(): Updater | null {
  const cap = (window as unknown as {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      Plugins?: { CapacitorUpdater?: Updater };
    };
  }).Capacitor;
  if (!cap?.isNativePlatform?.()) return null;
  return cap.Plugins?.CapacitorUpdater ?? null;
}

/** "0.1.5" >= "0.1.4" - kale numerieke vergelijking, genoeg voor onze
 *  versionName-vorm. Onparseerbaar = niet blokkeren. */
function atLeast(have: string, need: string): boolean {
  const h = have.split(".").map((n) => parseInt(n, 10));
  const n = need.split(".").map((x) => parseInt(x, 10));
  for (let i = 0; i < Math.max(h.length, n.length); i++) {
    const a = h[i] ?? 0, b = n[i] ?? 0;
    if (Number.isNaN(a) || Number.isNaN(b)) return true;
    if (a !== b) return a > b;
  }
  return true;
}

let checking = false;

async function checkOnce(): Promise<void> {
  const up = plugin();
  if (!up || checking) return;
  checking = true;
  try {
    const base = API_BASE || "";
    const r = await fetch(`${base}/updates/manifest.json`, { cache: "no-store" });
    if (!r.ok) return;
    const m: Manifest = await r.json();
    const cur = await up.current();
    if (!m.version || m.version === cur.bundle.version) return;
    if (m.min_native && !atLeast(cur.native, m.min_native)) {
      // Nieuwe UI vereist een nieuwere schil: overslaan tot de klant
      // de app-update installeert. Stil - hier valt niets te doen.
      return;
    }
    const bundle = await up.download({
      url: `${base}${m.path}`,
      version: m.version,
    });
    await up.next({ id: bundle.id });      // actief bij de volgende start
    // Oude bundels opruimen zodat de app niet volloopt.
    const { bundles } = await up.list();
    for (const b of bundles) {
      if (b.id !== bundle.id && b.version !== cur.bundle.version) {
        await up.delete({ id: b.id }).catch(() => undefined);
      }
    }
  } catch {
    // Netwerk weg, manifest kapot, plugin ontbreekt: stil overslaan.
    // Een mislukte update mag NOOIT de app raken.
  } finally {
    checking = false;
  }
}

export function initLiveUpdates(): void {
  const up = plugin();
  if (!up) return;
  // Bevestig dat deze bundel bruikbaar is (anders rolt de plugin terug).
  void up.notifyAppReady().catch(() => undefined);
  // Versies doorgeven aan de API-laag: het gebruiksdashboard laat zo
  // zien hoe snel een nieuwe bundel over de vloot uitrolt.
  void up.current()
    .then((c) => setAppVersions(c.native, c.bundle.version))
    .catch(() => undefined);
  void checkOnce();
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void checkOnce();
  });
}
